import React, { useState, useEffect, useCallback } from 'react';
import { 
  Modal, 
  Button, 
  Table, 
  Select, 
  Space, 
  Card, 
  Typography, 
  Row, 
  Col, 
  Avatar, 
  Tag, 
  message,
  Input,
  Divider
} from 'antd';
import { 
  DownloadOutlined, 
  UserOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { sarabunFont } from '../../sarabunFont';
import { useTranslation } from 'react-i18next';

import axios from 'axios';

const { Text, Title } = Typography;

const ScheduleModal = ({ isVisible, onClose, popupData = [], classChangeLogs = [] }) => {
  const { t } = useTranslation();
  
  // Student selection states
  const [showStudentSelection, setShowStudentSelection] = useState(false);
  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
 
  const API_URL = process.env.REACT_APP_API_URL;

  // Fetch students function
  const fetchStudents = useCallback(async (search = '') => {
    try {
      setLoadingStudents(true);
      const token = localStorage.getItem('token');

      const response = await axios.get(`${API_URL}/students/schedule-info-add`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: { search }
      });

      if (response.data.success) {
        const studentsList = response.data.students || [];
        setStudents(studentsList);
      } else {
        console.warn('[fetchStudents] Response returned success: false');
      }
    } catch (error) {
      console.error('[fetchStudents] Error:', error);
      message.error('Failed to fetch students');
    } finally {
      setLoadingStudents(false);
    }
  }, [API_URL]);

  // Load students on mount
  useEffect(() => {
    if (showStudentSelection) {
      fetchStudents();
    }
  }, [showStudentSelection, fetchStudents]);

  // Handle student search
  const handleStudentSearch = (value) => {
    setSearchTerm(value);
    fetchStudents(value);
  };

  // Helper function to get change reason display
  const getChangeReasonDisplay = (reason) => {
    const reasonMap = {
        "admin_adjust": { color: "#7DDCC0", text: "Admin" },
        "cancelled_by_student": { color: "#ff4757", text: "CL by S." },
        "cancelled_by_teacher": { color: "#ffa726", text: "CL by T." },
        "class_on_hold_by_student": { color: "#42a5f5", text: "On hold by S." },
        "class_on_hold_by_school": { color: "#66bb6a", text: "On hold by P." },
        "cancelled_same_day_teacher": { color: "#ef5350", text: "CSD (Teacher)" },
        "cancelled_same_day_student": { color: "#ef5350", text: "CSD (Student)" },
        "teacher_late": { color: "#FFD700", text: "Teacher Late" },
        "kease_cut": { color: "#ff00aa", text: "KEAES Cut" },
        "make_up": { color: "#ab47bc", text: "Make Up" },
        "others": { color: "#78909c", text: "Others" }
      };

    
    return reasonMap[reason] || { text: reason || '-', color: '#78909c' };
  };
  
  // Sort the data by date first, then by time
  const sortedData = [...(Array.isArray(popupData) ? popupData : [])].sort((a, b) => {
    const dateTimeA = new Date(`${a.date}T${a.schedule_time || '00:00'}`);
    const dateTimeB = new Date(`${b.date}T${b.schedule_time || '00:00'}`);
    return dateTimeA - dateTimeB;
  });
  
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date)) return 'Invalid Date';
  
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).format(date);
  };
  
  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
  };

  // Helper function to format minutes to hours
  const formatMinutesToHours = (minutes) => {
    if (!minutes || minutes === 0) return '0 hrs';
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}.${remainingMinutes} hrs` : `${hours} hrs`;
  };

  // Function to load Sarabun font into jsPDF
  const loadSarabunFont = (doc) => {
    doc.addFileToVFS('Sarabun.ttf', sarabunFont.src.split('base64,')[1]);
    doc.addFont('Sarabun.ttf', 'Sarabun', 'normal');
    doc.setFont('Sarabun');
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    loadSarabunFont(doc);

    const margin = 15;
    const pageWidth = doc.internal.pageSize.getWidth();
    const centerX = pageWidth / 2;

    // Updated color palette with teal theme
   const colors = {
        primary: [85, 85, 85],           // #555555 – Neutral dark gray for primary elements (buttons, titles)
        primaryDark: [68, 68, 68],       // #444444 – Even darker gray for borders or hover states
        primaryLight: [108, 117, 125],   // #6c757d – Bootstrap's secondary tone (used as light accent)
        secondary: [249, 249, 249],      // #f9f9f9 – Very light gray background
        accent: [222, 226, 230],         // #dee2e6 – Subtle divider or box highlight
        text: [51, 51, 51],              // #333333 – Main readable text color
        textLight: [102, 102, 102],      // #666666 – Subdued secondary text
        white: [255, 255, 255]           // #ffffff – White for clean backgrounds or contrast
      };


    const headerImage = new Image();
    headerImage.src = '/images/keaes150.png';

    headerImage.onload = () => {
      const imgAspectRatio = headerImage.width / headerImage.height;
      const targetHeight = 25;
      const targetWidth = targetHeight * imgAspectRatio;

      // Create modern header with teal theme
      const headerHeight = 35;
              // Draw soft rounded header background (no hard border)
        doc.setFillColor(249, 249, 249); // soft light gray
        doc.roundedRect(margin, margin, pageWidth - margin * 2, headerHeight, 4, 4, 'F');

        // Left section - Logo
        const logoX = margin + 10;
        const logoY = margin + 8;
        doc.addImage(headerImage, 'PNG', logoX, logoY, targetWidth, targetHeight);

        // Center section - Branding
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(13);
        doc.setTextColor(85, 85, 85); // #555
        doc.text('KEAES ACADEMICS', centerX, margin + 14, { align: 'center' });

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(11);
        doc.setTextColor(108, 117, 125); // #6c757d
        doc.text('Academic Schedule', centerX, margin + 21, { align: 'center' });

        // Right section - Contact Info (modern spacing)
        const rightX = pageWidth - margin - 10;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(102, 102, 102); // #666
        doc.text('Tel: 02 108 2922', rightX, margin + 9, { align: 'right' });
        doc.text('contact@keaes.com', rightX, margin + 14.5, { align: 'right' });
        doc.text('https://keaes.com', rightX, margin + 20, { align: 'right' });


      // The rest of the PDF generation logic remains the same but with updated colors
      let currentY = margin + 40;

      const classCode = popupData[0]?.class_code || 'N/A';
      const isPrivate = popupData[0]?.isprivate;
      const type = isPrivate === true ? 'Private' : 'Group';
      const totalHours = popupData?.[0]?.total_hours || 0;
      const subjects = [...new Set(popupData.map(slot => slot.subject_name))];
      const teachers = [...new Set(popupData.map(slot => slot.nickname).filter(Boolean))];

      // Section header with teal color
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...colors.primary);
      doc.text(`Course: ${classCode}`, margin + 2, currentY + 8);

      let infoY = currentY + 15;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...colors.text);

      const getPlatformText = (platform) => {
        switch(platform) {
          case 'online':
            return t('online');
          case 'onsite':
            return t('onsite');
          case 'athome':
            return t('athome');
          default:
            return platform || t('notAvailable');
        }
      };
      
      const platform = getPlatformText(popupData?.[0]?.platform);
      
      const sortedDates = popupData
        .map(item => new Date(item.date))
        .sort((a, b) => a - b);
      const startDate = formatDate(sortedDates[0]);
      const endDate = formatDate(sortedDates[sortedDates.length - 1]);

      if (selectedStudent) {
        const studentName = `${selectedStudent.firstname} ${selectedStudent.lastname}`;
        
        doc.text(`Student: ${studentName}`, margin + 2, infoY);
        doc.text(`Email: ${selectedStudent.email || 'N/A'}`, margin + 2, infoY + 6);
        doc.text(`Phone: ${selectedStudent.phone || 'N/A'}`, margin + 2, infoY + 12);
        
        // Class information (Right column)
        doc.text(`Type: ${type}`, centerX + 2, infoY);
        doc.text(`Total Hours: ${formatMinutesToHours(totalHours)}`, centerX + 2, infoY + 6);
        doc.text(`Platform: ${platform}`, centerX + 2, infoY + 12);
        
        
        infoY += 25;
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...colors.primary);
        doc.text(`Start Date: ${startDate}`, margin + 2, infoY);
        doc.text(`End Date: ${endDate}`, centerX + 2, infoY);
        
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...colors.text);
        
        currentY = infoY + 8;
      } else {
        // Class information only
        doc.text(`Course: ${classCode}`, margin + 2, infoY);
        doc.text(`Total Hours: ${formatMinutesToHours(totalHours)}`, centerX + 2, infoY);
        
        infoY += 6;
        doc.text(`Platform: ${platform}`, margin + 2, infoY);
   
        
        infoY += 10;
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...colors.primary);
        doc.text(`Start Date: ${startDate}`, margin + 2, infoY);
        doc.text(`End Date: ${endDate}`, centerX + 2, infoY);
        
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...colors.text);
        
        currentY = infoY + 8;
      }

      const tableStartY = currentY;

      const columns = [
        { header: t('day'), dataKey: 'schedule_day' },
        { header: t('date'), dataKey: 'date', render: formatDate },
        {
          header: t('time'),
          dataKey: 'schedule_time',
          render: (text, record) => `${formatTime(record.schedule_time)} - ${formatTime(record.end_time)}`
        },
        { 
              header: t('hours'), 
              dataKey: 'schedule_hour',
              render: (hourString) => {
                if (!hourString) return '0:00 hr';
                
                // Parse the current format like "2h 0m" or "2h 45m"
                const match = hourString.match(/(\d+)h\s*(\d+)m/);
                if (match) {
                  const hours = match[1];
                  const minutes = match[2].padStart(2, '0');
                  return `${hours}:${minutes} hr`;
                }
                
                // Fallback if format doesn't match
                return hourString;
              }
            },
        { header: t('subjectName'), dataKey: 'subject_name' },
        { header: t('teacherName'), dataKey: 'nickname' },
        { 
          header: t('classchange'), 
          dataKey: 'reason',
          render: (text, record) => {
            const changeLog = classChangeLogs.find(ccl => ccl.classid === record.classid);
            const reasonDisplay = changeLog
              ? getChangeReasonDisplay(changeLog.changereason) 
              : { text: '-', color: 'black' };
            return reasonDisplay.text;
          }
        }
      ];

      

      doc.autoTable({
        startY: tableStartY,
        head: [columns.map(col => col.header)],
        body: sortedData.map(row =>
          columns.map(col => col.render ? col.render(row[col.dataKey], row) : row[col.dataKey])
        ),
        theme: 'grid',
        styles: {
          font: 'Sarabun',
          fontStyle: 'normal',
          textColor: [60, 60, 60],          // modern dark gray
          lineColor: [230, 230, 230],       // soft gray borders

        },
        headStyles: {
          fillColor: [103, 141, 91],         // modern green (muted, not neon)
          textColor: [255, 255, 255],       // white
          fontStyle: 'bold',
          halign: 'center',
        },
        alternateRowStyles: {
          fillColor: [248, 248, 248]        // very light gray for zebra rows
        },
        margin: { top: 20, left: margin, right: margin }
      });


      const pageCount = doc.internal.getNumberOfPages();
      
      for(let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        
        doc.setTextColor(...colors.primary);
        doc.setFontSize(8);
        doc.text(
          `Page ${i} of ${pageCount}`, 
          doc.internal.pageSize.getWidth() / 2, 
          doc.internal.pageSize.getHeight() - 15,
          { align: 'center' }
        );
        
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...colors.textLight);
        const footerY = doc.internal.pageSize.getHeight() - 8;
        const currentDate = new Date().toLocaleDateString('en-GB');
        doc.text(`Printed on ${currentDate}`, centerX, footerY, { align: 'center' });
      }

      const fileName = selectedStudent 
        ? `${classCode}_${selectedStudent.firstname}_${selectedStudent.lastname}.pdf`
        : `${classCode}.pdf`;
      
      doc.save(fileName);
      message.success('PDF downloaded successfully!');
    };

    headerImage.onerror = () => {
      console.error('Failed to load the header image.');
      message.error('Failed to load header image');
    };
  };
  
  const columns = [
    { title: t('day'), dataIndex: 'schedule_day', key: 'schedule_day' },
    { title: t('date'), dataIndex: 'date', key: 'date', render: formatDate },
    {
      title: t('time'),
      key: 'time',
      render: (text, record) => `${formatTime(record.schedule_time)} - ${formatTime(record.end_time)}`
    },
    { title: t('hours'), dataIndex: 'schedule_hour', key: 'schedule_hour' },
    { title: t('subjectName'), dataIndex: 'subject_name', key: 'subject_name' },
    { 
      title: 'Platform', 
      dataIndex: 'platform', 
      key: 'platform',
      render: (platform) => {
        const platformConfig = {
          online: { text: 'Online', color: '#52c41a' },
          onsite: { text: 'Onsite', color: '#1890ff' },
          athome: { text: 'At Home', color: '#fa8c16' }
        };
        const config = platformConfig[platform] || { text: platform || 'N/A', color: '#d9d9d9' };
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    { title: t('teacherName'), dataIndex: 'nickname', key: 'nickname' },
    { 
      title: t('classchange'), 
      key: 'reason',
      render: (text, record) => {
        const changeLog = classChangeLogs.find(ccl => ccl.classid === record.classid);
        const reasonDisplay = changeLog
          ? getChangeReasonDisplay(changeLog.changereason) 
          : { text: '-', color: '#78909c' };
        return (
          <Tag style={{ color: reasonDisplay.color, borderColor: reasonDisplay.color }}>
            {reasonDisplay.text}
          </Tag>
        );
      }
    },
    { 
      title: t('note'), 
      key: 'note',
      render: (text, record) => {
        const changeLog = classChangeLogs.find(ccl => ccl.classid === record.classid);
        return (
          <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {changeLog?.note || '-'}
          </div>
        );
      }
    }
  ];

  // Determine which view to show
  const getCurrentView = () => {
    if (showStudentSelection) return 'student';
    return 'main';
  };

  const currentView = getCurrentView();

  // Get class summary data
  const classSummary = {
    classCode: sortedData?.[0]?.class_code || 'N/A',
    totalSessions: sortedData.length,
    subjects: [...new Set(sortedData.map(slot => slot.subject_name))],
    teachers: [...new Set(sortedData.map(slot => slot.nickname).filter(Boolean))],
    platform: sortedData?.[0]?.platform || 'N/A',
    isPrivate: sortedData?.[0]?.isprivate,
    totalHours: sortedData?.[0]?.total_hours || 0
  };

  return (
    <Modal
      open={isVisible}
      onCancel={onClose}
      footer={null}
      title={null}
      width={currentView === 'main' ? 900 : 1000}
      styles={{
        header: { display: 'none' },
        body: { padding: '24px' }
      }}
    >
      {currentView === 'main' && (
        <>
          {/* Modern Header */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(125, 220, 192, 0.1) 0%, rgba(244, 248, 187, 0.1) 100%)',
            borderRadius: '16px',
            padding: '20px',
            marginBottom: '24px',
            border: '1px solid rgba(125, 220, 192, 0.2)'
          }}>
            <Row align="middle" justify="space-between">
              <Col>
                <Space align="center">
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: '#7DDCC0',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <CalendarOutlined style={{ fontSize: '24px', color: 'white' }} />
                  </div>
                  <div>
                    <Title level={3} style={{ margin: 0, color: '#2d3748' }}>
                      {classSummary.classCode}
                    </Title>
                    <Text style={{ color: '#718096' }}>
                      {classSummary.totalSessions} sessions • {formatMinutesToHours(classSummary.totalHours)}
                    </Text>
                  </div>
                </Space>
              </Col>
              <Col>
                <Space>
                  <Tag color="#7DDCC0" style={{ borderRadius: '20px', padding: '4px 12px' }}>
                    {classSummary.isPrivate ? 'Private' : 'Group'}
                  </Tag>
                  <Tag color="#5A9D8A" style={{ borderRadius: '20px', padding: '4px 12px' }}>
                    {classSummary.platform}
                  </Tag>
                </Space>
              </Col>
            </Row>

            {selectedStudent && (
              <>
                <Divider style={{ margin: '16px 0', borderColor: 'rgba(125, 220, 192, 0.3)' }} />
                <Row align="middle" justify="space-between">
                  <Col>
                    <Space align="center">
                      <Avatar 
                        size={40}
                        style={{ 
                          background: '#7DDCC0',
                          border: '2px solid white'
                        }}
                        icon={<UserOutlined />}
                      >
                        {selectedStudent.firstname?.[0]?.toUpperCase()}
                      </Avatar>
                      <div>
                        <Text strong style={{ color: '#2d3748' }}>
                          {selectedStudent.firstname} {selectedStudent.lastname}
                        </Text>
                        <br />
                        <Text style={{ color: '#718096', fontSize: '12px' }}>
                          {selectedStudent.email} • {selectedStudent.phone}
                        </Text>
                      </div>
                    </Space>
                  </Col>
                  <Col>
                    <Button 
                      size="small" 
                      onClick={() => setSelectedStudent(null)}
                      style={{
                        borderColor: '#7DDCC0',
                        color: '#5A9D8A',
                        borderRadius: '8px'
                      }}
                    >
                      Change Student
                    </Button>
                  </Col>
                </Row>
              </>
            )}
          </div>

          {/* Action Buttons */}
         <div style={{ marginBottom: '20px' }}>
  <Space>
    <Button 
      type="primary"
      icon={<UserOutlined />}
      onClick={() => setShowStudentSelection(true)}
      style={{
        background: '#7DDCC0',
        border: 'none',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(125, 220, 192, 0.3)'
      }}
    >
      {selectedStudent ? 'Change Student' : 'Select Student'}
    </Button>
    

    
    <Button 
      icon={<DownloadOutlined />} 
      onClick={handleDownloadPDF}
      style={{
        borderColor: '#7DDCC0',
        color: '#5A9D8A',
        borderRadius: '8px'
      }}
    >
      {t('downloadPDF')}
    </Button>
  </Space>
</div>

          {/* Schedule Table */}
          <Table
            dataSource={sortedData}
            columns={columns}
            rowKey={(record) => record.id}
            pagination={false}
            size="small"
            scroll={{ x: 'max-content' }}
            style={{
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0 2px 8px rgba(125, 220, 192, 0.1)'
            }}
          />
        </>
      )}

      {currentView === 'student' && (
        <>
          {/* Student Selection Header */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(125, 220, 192, 0.1) 0%, rgba(244, 248, 187, 0.1) 100%)',
            borderRadius: '16px',
            padding: '20px',
            marginBottom: '24px',
            border: '1px solid rgba(125, 220, 192, 0.2)'
          }}>
            <Row justify="space-between" align="middle">
              <Col>
                <Space align="center">
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: '#7DDCC0',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <UserOutlined style={{ fontSize: '24px', color: 'white' }} />
                  </div>
                  <div>
                    <Title level={4} style={{ margin: 0, color: '#2d3748' }}>Select Student</Title>
                    <Text style={{ color: '#718096' }}>Choose a student for this schedule</Text>
                  </div>
                </Space>
              </Col>
              <Col>
                <Button 
                  onClick={() => setShowStudentSelection(false)}
                  style={{
                    borderColor: '#7DDCC0',
                    color: '#5A9D8A',
                    borderRadius: '8px'
                  }}
                >
                  Back to Schedule
                </Button>
              </Col>
            </Row>
          </div>

          <Card style={{ borderRadius: '12px', border: '1px solid rgba(125, 220, 192, 0.2)' }}>
            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ color: '#2d3748' }}>Search and Select Student:</Text>
            </div>
            
            <Select
              showSearch
              placeholder="Search by name, nickname, email, or phone..."
              style={{ width: '100%', marginBottom: 16 }}
              loading={loadingStudents}
              onSearch={handleStudentSearch}
              onChange={(value) => {
                const student = students.find(s => s.value === value);
                if (student) {
                  const mappedStudent = {
                    userid: student.value,
                    firstname: student.label.split(' ')[0] || '',
                    lastname: student.label.split(' ').slice(1).join(' ') || '',
                    nickname: '',
                    email: student.contactInfo?.email || '',
                    phone: student.contactInfo?.phone || '',
                    address: student.contactInfo?.address || '',
                    status: student.status || 'active',
                    date_of_birth: student.date_of_birth
                  };
                  setSelectedStudent(mappedStudent);
                }
              }}
              filterOption={false}
              notFoundContent={loadingStudents ? 'Loading...' : 'No students found'}
            >
              {students.map(student => (
                <Select.Option key={student.value} value={student.value}>
                  <div>
                    <div style={{ fontWeight: 'bold' }}>
                      {student.label}
                    </div>
                  </div>
                </Select.Option>
              ))}
            </Select>

            {selectedStudent && (
              <Card 
                size="small" 
                style={{ 
                  background: 'linear-gradient(135deg, rgba(125, 220, 192, 0.1) 0%, rgba(244, 248, 187, 0.1) 100%)',
                  border: '1px solid rgba(125, 220, 192, 0.3)',
                  borderRadius: '12px'
                }}
              >
                <div style={{ marginBottom: 12 }}>
                  <Text strong style={{ color: '#2d3748' }}>Selected Student:</Text>
                </div>
                
                <Row gutter={[24, 12]}>
                <Col span={12}>
                    <div style={{ marginBottom: 12 }}>
                      <Text strong style={{ color: '#2d3748' }}>Name:</Text>
                      <br />
                      <Text style={{ color: '#718096' }}>
                        {selectedStudent.firstname} {selectedStudent.lastname}
                        {selectedStudent.nickname && (
                          <span style={{ color: '#7DDCC0' }}> ({selectedStudent.nickname})</span>
                        )}
                      </Text>
                    </div>
                    
                    <div style={{ marginBottom: 12 }}>
                      <Text strong style={{ color: '#2d3748' }}>Email:</Text>
                      <br />
                      <Text style={{ color: '#718096' }}>{selectedStudent.email || 'N/A'}</Text>
                    </div>

                    <div style={{ marginBottom: 12 }}>
                      <Text strong style={{ color: '#2d3748' }}>Phone:</Text>
                      <br />
                      <Text style={{ color: '#718096' }}>{selectedStudent.phone || 'N/A'}</Text>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ marginBottom: 12 }}>
                      <Text strong style={{ color: '#2d3748' }}>Status:</Text>
                      <br />
                      <Tag 
                        color={selectedStudent.status === 'active' ? '#52c41a' : '#ff4d4f'}
                        style={{ borderRadius: '12px', padding: '2px 8px' }}
                      >
                        {selectedStudent.status?.toUpperCase()}
                      </Tag>
                    </div>

                    <div style={{ marginBottom: 12 }}>
                      <Text strong style={{ color: '#2d3748' }}>Date of Birth:</Text>
                      <br />
                      <Text style={{ color: '#718096' }}>
                        {selectedStudent.date_of_birth 
                          ? new Date(selectedStudent.date_of_birth).toLocaleDateString('en-GB')
                          : 'N/A'
                        }
                      </Text>
                    </div>
                    
                    <div style={{ marginBottom: 12 }}>
                      <Text strong style={{ color: '#2d3748' }}>Address:</Text>
                      <br />
                      <Text style={{ color: '#718096' }}>{selectedStudent.address || 'N/A'}</Text>
                    </div>
                  </Col>
                </Row>
                
                <div style={{ marginTop: 20, textAlign: 'right' }}>
                  <Space>
                    <Button 
                      onClick={() => setSelectedStudent(null)}
                      style={{
                        borderColor: '#ff4757',
                        color: '#ff4757',
                        borderRadius: '8px'
                      }}
                    >
                      Clear Selection
                    </Button>
                    <Button 
                      type="primary" 
                      onClick={() => setShowStudentSelection(false)}
                      style={{
                        background: '#7DDCC0',
                        border: 'none',
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(125, 220, 192, 0.3)'
                      }}
                    >
                      Confirm Selection
                    </Button>
                  </Space>
                </div>
              </Card>
            )}
          </Card>
        </>
      )}
   
    </Modal>
    
  );
  
};

export default ScheduleModal;